/**
 * CCO › Mapa de Comando: a rede de saneamento inteira numa maquete 3D viva — Nexora Grid (US-4402).
 *
 * Criado em: 01/11/2026 · Última modificação: 02/11/2026 · Versão: 1.1.0
 *
 * Num só lugar, no estilo de sala de controle (ISA-101: cor só para anormalidade):
 * - os setores com os equipamentos reais em 3D (poço, motobomba, reservatório, VRP, macromedidor, ponto de pressão),
 *   o estado de cada um e a telemetria ao vivo (nível do reservatório, pressão da rede, vazão, corrente);
 * - os agentes de IA (hologramas) e as pessoas (sólidas, uniformizadas), com os eventos virando pulsos de luz;
 * - alarmes, agentes, linha do tempo e o fluxo de eventos (só a intenção, nunca o texto da conversa).
 *
 * **Fontes:** o modelo ao vivo do CCO (`useModelo`: tempo real + cadastro + alarmes) e
 * `GET /api/v1/observabilidade/local` (sem `ativo_id` = operadora inteira) com o SSE `/local/stream`.
 * **Permissão:** `ver_observabilidade_local` (a mesma da CCO › Local).
 * **Só observação:** nenhuma manobra aqui; "Abrir no CCO Clássico" leva ao equipamento (regra inviolável 4).
 * **Layout e movimento do protótipo** (claude.ai, 01/11): topo com relógio, contadores, colunas laterais, linha do tempo dos
 * últimos 10 min andando a cada segundo, fluxo com o cartão novo acendendo e agentes acendendo no evento ao vivo.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, ErroApi, lerFluxo } from "../api";
import { NOME_NIVEL, notaEquipamento } from "./anomalia";
import { type Ator, type EventoLocal } from "./observabilidade";
import { useCco } from "./contexto";
import { fmt, TIPO_NOME, useModelo, type Equipamento } from "./dados";
import { CenaMapa, type AlvoCena, type AtorCena, type PlacaCena, type SetorCena } from "./MapaComando3d";
import { canalDeMensagem, codigoDoSetor, COR_EVENTO, COR_SEGURANCA, COR_TELEGRAM, comAtividade, corDoEvento, emAcao, equipeDoMapa, estadoDoNivel, medidaPrincipal, modeloDaPessoa, modeloDoTipo, piorEstado, rotaDoEvento, situacaoDoAgente,
  tipoDoCadastro, tituloDoSetor,
  type EstadoMapa } from "./regrasMapa";
import { FORMA, fmtValor, nomeMedida, unidadeMedida } from "./medidas";
import { chave } from "./useTempoReal";
import "./mapa-comando.css";

type Resposta = { eventos: EventoLocal[]; atores: Ator[] };
const NOME_ESTADO: Record<EstadoMapa, string> = { ok: "Normal", atencao: "Atenção", alarme: "Alarme", offline: "Sem dados" };
const NOME_TIPO_ATOR: Record<EventoLocal["tipo_ator"], string> = { humano: "Pessoa", agente: "Agente de IA", regra: "Regra", ronda: "Ronda",
  sistema: "Sistema", campo: "Campo" };
const JANELA_MIN = 10;
/** Setor com leitura nos últimos 60 s manda dados "contínuos" ao CCO (arco aceso, como o MQTT do protótipo). */
const CONTINUO_MS = 60000;
const hora = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—");
const idade = (iso: string | undefined) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  return s < 90 ? `há ${Math.round(s)} s` : s < 5400 ? `há ${Math.round(s / 60)} min` : `há ${Math.round(s / 3600)} h`;
};
const valorComUnidade = (m: string, v: number) => `${fmtValor(m, v)} ${unidadeMedida(m)}`.trim();

export function MapaComando() {
  const { empresa, vivo, alarmes, ativos } = useCco();
  const modelo = useModelo();
  const palco = useRef<HTMLDivElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const cena = useRef<CenaMapa | null>(null);
  const [pronta, setPronta] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [eventos, setEventos] = useState<EventoLocal[]>([]);
  const [atores, setAtores] = useState<Ator[]>([]);
  const [selecao, setSelecao] = useState<AlvoCena | null>(null);
  const [filtro, setFiltro] = useState<EstadoMapa | null>(null);
  const [dica, setDica] = useState<{ alvo: AlvoCena; x: number; y: number } | null>(null);
  const [girar, setGirar] = useState(!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  const [seguir, setSeguir] = useState(false);
  const [cheia, setCheia] = useState(false);
  const [agora, setAgora] = useState(Date.now());

  /* setores e equipamentos da operadora, com o estado na maquete */
  const setores = useMemo(() => {
    const variasCidades = modelo.cidades.length > 1;
    const lista: ItemSetor[] = modelo.cidades.flatMap((c) => c.setores.map((s) => {
      const equipamentos = s.equipamentos.map((e) => ({ e, estado: estadoDoNivel(e.nota.nivel, e.velho) }));
      return { id: `${c.municipio.codigo}/${s.codigo}`, codigo: codigoDoSetor(s.codigo), titulo: variasCidades ? `${s.titulo} · ${c.municipio.nome}` : s.titulo,
        setor: s, estado: piorEstado(equipamentos.map((x) => x.estado)), equipamentos };
    }));
    // o que está só no cadastro (sem telemetria) entra como "sem dados", no setor do cadastro: assim a rede importada aparece inteira
    const comTelemetria = new Set(lista.flatMap((s) => s.equipamentos.map((x) => x.e.device)));
    const porCodigo = new Map(lista.map((s) => [s.codigo, s]));
    ativos.filter((a) => a.situacao !== "rejeitado").forEach((a) => {
      const tipo = tipoDoCadastro(a.dados.tipo);
      const device = a.dados.device_id || `ativo:${a.id}`;
      if (!tipo || comTelemetria.has(device)) return;
      const codigo = codigoDoSetor(a.dados.setor);
      let s = porCodigo.get(codigo);
      if (!s) {
        s = { id: `cadastro/${codigo}`, codigo, titulo: tituloDoSetor(codigo), setor: { ind: { pressaoMin: null } }, estado: "ok", equipamentos: [] };
        porCodigo.set(codigo, s);
        lista.push(s);
      }
      const nome = a.dados.nome ? `${a.dados.identificacao} · ${a.dados.nome}` : a.dados.identificacao;
      s.equipamentos.push({ e: { device, nome, tipo, setor: codigo, cidade: "", medidas: new Set<string>(), nota: notaEquipamento([], true), ativos: [a], velho: true },
        estado: "offline" });
    });
    lista.forEach((s) => { s.estado = piorEstado(s.equipamentos.map((x) => x.estado)); });
    const PESO: Record<EstadoMapa, number> = { alarme: 0, atencao: 1, offline: 2, ok: 3 };
    return lista.filter((s) => s.equipamentos.length).sort((a, b) => PESO[a.estado] - PESO[b.estado] || a.titulo.localeCompare(b.titulo, "pt-BR"));
  }, [modelo, ativos]);
  const equipamentos = useMemo(() => setores.flatMap((s) => s.equipamentos), [setores]);
  const porDevice = useMemo(() => new Map(equipamentos.map((x) => [x.e.device, x])), [equipamentos]);

  /* cena 3D: monta uma vez e carrega a biblioteca de modelos (US-4401) */
  useEffect(() => {
    if (!palco.current) return;
    const c = new CenaMapa(palco.current);
    cena.current = c;
    c.aoSelecionar = (a) => setSelecao(a);
    c.aoPassar = (a, x, y) => setDica(a ? { alvo: a, x, y } : null);
    c.carregar().then(() => setPronta(true)).catch(() => setErro("Não foi possível carregar os modelos 3D. Recarregue a página."));
    return () => { c.descartar(); cena.current = null; };
  }, []);
  useEffect(() => { cena.current?.girar(girar); }, [girar]);

  /* estrutura da rede (remonta só se mudar) */
  useEffect(() => {
    if (!pronta || !cena.current) return;
    const lista: SetorCena[] = setores.map((s) => ({ id: s.id, titulo: s.titulo, estado: s.estado,
      equipamentos: s.equipamentos.map(({ e, estado }) => ({ id: e.device, nome: e.nome, estado,
        modelo: modeloDoTipo(e.tipo, e.ativos[0]?.dados.tipo ?? vivo.tipos.get(e.device), e.ativos[0]?.dados.identificacao) })) }));
    cena.current.montarRede(lista);
  }, [pronta, setores, vivo.tipos]);

  /* telemetria: placa, régua de nível e pressão da rede por setor */
  useEffect(() => {
    if (!pronta || !cena.current) return;
    const placas = new Map<string, PlacaCena>();
    equipamentos.forEach(({ e }) => {
      const m = medidaPrincipal(e.tipo, e.medidas);
      const v = m ? modelo.valor(e.device, m) : undefined;
      const principal = m ? `${nomeMedida(m)} ${v === undefined ? "—" : (e.velho ? "último · " : "") + valorComUnidade(m, v)}` : "Sem telemetria";
      const leitura = m ? vivo.leituras.get(chave(e.device, m)) : undefined;
      const resto = [...e.medidas].filter((x) => x !== m).sort().slice(0, 5).map((x) => {
        const vx = modelo.valor(e.device, x);
        return `${nomeMedida(x)}: ${vx === undefined ? "—" : valorComUnidade(x, vx)}`;
      });
      if (leitura) resto.push(`leitura ${idade(leitura.timestamp)}`);
      if (!e.medidas.size) resto.push(`só no cadastro${e.ativos[0] ? ` (${e.ativos[0].situacao})` : ""}`);
      placas.set(e.device, { principal, resto, nivelPct: e.tipo === "reservatorio" ? modelo.valor(e.device, "nivel_reservatorio_pct") ?? null : null });
    });
    const pressoes = new Map(setores.map((s) => [s.id, s.setor.ind.pressaoMin]));
    const continuos = new Set(setores.filter((s) => s.equipamentos.some(({ e }) => [...e.medidas].some((m) => {
      const t = Date.parse(vivo.leituras.get(chave(e.device, m))?.timestamp ?? "");
      return Date.now() - t < CONTINUO_MS;
    }))).map((s) => s.id));
    cena.current.atualizar(placas, pressoes, continuos);
  }, [pronta, equipamentos, setores, modelo, vivo.leituras, vivo.versao]);

  /* pessoas e agentes: eventos da operadora inteira, ao vivo */
  const pulsar = useCallback((lista: EventoLocal[]) => {
    const c = cena.current;
    if (!c) return;
    lista.forEach((e) => {
      const rota = rotaDoEvento(e, (d) => porDevice.has(d));
      const msg = e.tipo_ator === "humano" && canalDeMensagem(e.canal);
      c.pulsar(rota, corDoEvento(e), msg ? { texto: e.acao.replace(/_/g, " "), canal: /whats/i.test(e.canal) ? "WhatsApp" : "Telegram",
        classe: /telegram/i.test(e.canal) ? "tele" : "" } : undefined);
    });
    const alvo = [...lista].reverse().map((e) => e.device_ids.find((d) => porDevice.has(d))).find(Boolean);
    if (seguir && alvo) c.voarPara({ tipo: "equipamento", id: alvo });
  }, [porDevice, seguir]);

  useEffect(() => {
    if (!empresa) return;
    const carregar = () => api<Resposta>(`/v1/observabilidade/local?empresa=${empresa}&horas=24`)
      .then((r) => { setEventos(r.eventos); setAtores(r.atores); setErro(null); })
      .catch((x) => setErro(x instanceof ErroApi ? x.message : "Falha de rede."));
    void carregar();
    const r = window.setInterval(() => void carregar(), 120000);
    return () => window.clearInterval(r);
  }, [empresa]);

  useEffect(() => {
    if (!empresa) return;
    const controle = new AbortController();
    let vivoFluxo = true;
    (async () => {
      while (vivoFluxo) {
        try {
          await lerFluxo(`/v1/observabilidade/local/stream?empresa=${empresa}`, (e) => {
            if (e.evento !== "eventos") return;
            const novos = e.dados as EventoLocal[];
            setEventos((atual) => {
              const ids = new Set(atual.map((x) => x.id));
              return [...atual, ...novos.filter((x) => !ids.has(x.id))].slice(-2000);
            });
            pulsar(novos);
          }, controle.signal);
        } catch { if (!vivoFluxo) return; }
        await new Promise((r) => setTimeout(r, 5000));
      }
    })();
    return () => { vivoFluxo = false; controle.abort(); };
  }, [empresa, pulsar]);

  /* ao abrir, os últimos eventos acendem a maquete */
  const jaPulsou = useRef(false);
  useEffect(() => {
    if (!pronta || jaPulsou.current || !eventos.length || !equipamentos.length) return;
    jaPulsou.current = true;
    pulsar(eventos.slice(-6));
  }, [pronta, eventos, equipamentos.length, pulsar]);

  useEffect(() => { const r = window.setInterval(() => setAgora(Date.now()), 1000); return () => window.clearInterval(r); }, []);

  const equipe = useMemo(() => comAtividade(equipeDoMapa(atores), eventos), [atores, eventos]);
  const pessoas = useMemo(() => atores.filter((a) => a.tipo_ator === "humano"), [atores]);
  useEffect(() => {
    if (!pronta || !cena.current) return;
    const ag: AtorCena[] = equipe.filter((a) => a.id !== "gestor").slice(0, 10)
      .map((a) => ({ id: a.id, nome: a.disponivel ? a.curto : `${a.curto} (indisp.)`, modelo: "operador",
        estado: a.disponivel && emAcao(a.ultimo, agora) ? "ativo" : "ocioso" }));
    const pe: AtorCena[] = [...pessoas].sort((a, b) => (b.ultimo ?? "").localeCompare(a.ultimo ?? "")).slice(0, 8)
      .map((p) => ({ id: p.ator, nome: p.ator, modelo: modeloDaPessoa(p.papel), estado: emAcao(p.ultimo, agora) ? "ativo" : "ocioso" }));
    cena.current.definirAtores(ag, pe);
  }, [pronta, equipe, pessoas, agora]);

  /* seleção e filtro */
  useEffect(() => {
    cena.current?.selecionar(selecao?.tipo === "equipamento" ? selecao.id : null, selecao?.tipo === "setor" ? selecao.id : null);
    if (selecao) cena.current?.voarPara(selecao);
  }, [selecao]);
  useEffect(() => {
    cena.current?.filtrar(filtro ? new Set(equipamentos.filter((x) => x.estado === filtro).map((x) => x.e.device)) : null);
  }, [filtro, equipamentos]);

  const telaCheia = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void caixa.current?.requestFullscreen?.().catch(() => undefined);
  };
  useEffect(() => {
    const f = () => setCheia(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", f);
    return () => document.removeEventListener("fullscreenchange", f);
  }, []);

  const conta = (e: EstadoMapa) => equipamentos.filter((x) => x.estado === e).length;
  const naoReconhecidos = alarmes.filter((a) => !a.reconhecido).sort((a, b) => a.prioridade - b.prioridade);
  const medidasTotal = equipamentos.reduce((t, x) => t + x.e.medidas.size, 0);
  const eventosJanela = eventos.filter((e) => e.instante && agora - Date.parse(e.instante) <= JANELA_MIN * 60000);
  const comandos24 = eventos.filter((e) => e.tipo_ator === "humano").length;
  const regras24 = eventos.filter((e) => e.tipo_ator === "regra").length;
  const cidades = [...new Set(modelo.cidades.map((c) => c.municipio.nome))].join(" · ");
  const recentes = [...eventos].reverse().slice(0, 8);
  const nomeDe = (device: string) => porDevice.get(device)?.e.nome ?? device.toUpperCase();

  return (
    <div className={`mc${cheia ? " cheia" : ""}`} ref={caixa}>
      <div className="mc-barra" role="toolbar" aria-label="Controles do mapa">
        <div className="mc-marca"><h1>Mapa de <b>Comando</b></h1><span>{[empresa, cidades].filter(Boolean).join(" · ")}</span></div>
        <span className="mc-espaco" />
        <button type="button" onClick={() => cena.current?.visaoGeral()}>Visão geral</button>
        <button type="button" aria-pressed={seguir} onClick={() => setSeguir((s) => !s)}>Seguir eventos</button>
        <button type="button" aria-pressed={girar} onClick={() => setGirar((g) => !g)}>Girar</button>
        <button type="button" onClick={telaCheia}>{cheia ? "Sair da tela cheia" : "Tela cheia"}</button>
        <span className="mc-ronly"><i aria-hidden="true" />Só observação · manobra no CCO Clássico</span>
        <span className="mc-relogio">{new Date(agora).toLocaleTimeString("pt-BR")}</span>
      </div>
      <nav className="mc-chips" aria-label="Filtros">
        {(["alarme", "atencao", "offline"] as EstadoMapa[]).map((e) => (
          <button key={e} type="button" className={`mc-chip ${e}`} aria-pressed={filtro === e} onClick={() => setFiltro(filtro === e ? null : e)}>
            {e === "alarme" ? "Em alarme" : NOME_ESTADO[e]} <b>{conta(e)}</b></button>))}
        <span className="mc-chip">Equipamentos <b>{equipamentos.length}</b></span>
        <span className="mc-chip">Sensores e atuadores <b>{medidasTotal}</b></span>
        <span className="mc-chip">Agentes de IA <b>{equipe.filter((a) => a.disponivel).length}</b></span>
        <span className="mc-chip">Comandos 24 h <b>{comandos24}</b></span>
        <span className="mc-chip">Regras disparadas <b>{regras24}</b></span>
      </nav>
      {erro && <p className="mc-aviso" role="alert">{erro}</p>}

      <div className="mc-corpo">
        <aside className="mc-col" aria-label="Setores e seleção">
          <section className="mc-bloco">
            <h2>Setores <small>{equipamentos.length} equipamentos</small></h2>
            <div className="mc-lista">
              {setores.map((s) => {
                const c = (e: EstadoMapa) => s.equipamentos.filter((x) => x.estado === e).length;
                const partes = [c("alarme") && `${c("alarme")} alarme`, c("atencao") && `${c("atencao")} atenção`, c("offline") && `${c("offline")} sem dados`].filter(Boolean);
                return (
                  <button key={s.id} type="button" className="mc-setor" aria-pressed={selecao?.tipo === "setor" && selecao.id === s.id}
                    onClick={() => setSelecao({ tipo: "setor", id: s.id })}>
                    <span className={`mc-est ${s.estado}`} /><span className="nm">{s.titulo}</span><span className="ct">{s.equipamentos.length} equip.</span>
                    <span /><span className="ct det">{partes.length ? partes.join(" · ") : `${s.equipamentos.length} normais`}
                      {s.setor.ind.pressaoMin !== null && ` · rede ${fmt(s.setor.ind.pressaoMin, 1)} mca`}</span>
                  </button>);
              })}
              {!setores.length && <p className="mc-vazio">Aguardando a telemetria da operadora…</p>}
            </div>
          </section>
          <Selecao selecao={selecao} limpar={() => setSelecao(null)} porDevice={porDevice} setores={setores} atores={atores} eventos={eventos}
            valor={modelo.valor} />
        </aside>

        <div className="mc-palco" ref={palco} aria-label="Maquete 3D da rede">
          {!pronta && !erro && <div className="mc-carregando">Carregando maquete 3D…</div>}
          {dica && <Dica dica={dica} porDevice={porDevice} setores={setores} />}
          <div className="mc-legenda" aria-hidden="true">
            <span><i className="ok" />Normal</span><span><i className="atencao" />Atenção</span><span><i className="alarme" />Alarme</span>
            <span><i className="offline" />Sem dados</span><span><i className="holo" />Agente de IA</span><span><i className="pessoa" />Pessoa</span>
          </div>
        </div>

        <aside className="mc-col" aria-label="Alarmes e agentes">
          <section className="mc-bloco">
            <h2>Alarmes <small>{naoReconhecidos.length} sem reconhecer</small></h2>
            <div className="mc-lista">
              {naoReconhecidos.slice(0, 12).map((a) => (
                <button key={`${a.device_id}-${a.tipo}`} type="button" className={`mc-alm p${a.prioridade}`} onClick={() => porDevice.has(a.device_id) && setSelecao({ tipo: "equipamento", id: a.device_id })}>
                  <span className="faixa" /><span className="pr">{FORMA[a.prioridade]} P{a.prioridade}</span>
                  <span className="tx"><b>{nomeDe(a.device_id)}</b> {a.descricao || a.tipo.replace(/_/g, " ").toLowerCase()}</span>
                  <span className="hr">{hora(a.ultimo)}</span>
                </button>))}
              {!naoReconhecidos.length && <p className="mc-vazio">Nenhum alarme sem reconhecer.</p>}
            </div>
          </section>
          <section className="mc-bloco">
            <h2>Agentes <small>fazendo · feito · último</small></h2>
            <div className="mc-lista">
              {equipe.map((a) => {
                const sit = situacaoDoAgente(a, agora, hora);
                const ativo = sit.ativo;
                return (
                  <button key={a.id} type="button" className="mc-ag" title={a.papel} onClick={() => setSelecao({ tipo: "agente", id: a.id })}>
                    <span className={`holo${a.id === "gestor" ? " gestor" : ""}`} /><span className="nm">{a.nome}</span>
                    <span className={`pill${ativo ? " ativo" : ""}`}>{sit.rotulo}</span>
                    <span className="papel">{a.papel}</span>
                    <span className="nums">{a.disponivel ? <><span>fazendo {ativo ? 1 : 0}</span><span>feito {a.eventos}</span></> : a.motivo}</span>
                    {a.disponivel && sit.detalhe && <span className="papel">{sit.detalhe}</span>}
                  </button>);
              })}
            </div>
          </section>
        </aside>
      </div>

      <footer className="mc-rodape">
        <LinhaDoTempo eventos={eventosJanela} agora={agora} aoEscolher={(e) => {
          pulsar([e]);
          const d = e.device_ids.find((x) => porDevice.has(x));
          if (d) setSelecao({ tipo: "equipamento", id: d });
        }} />
        <section className="mc-fluxo" aria-label="Fluxo de eventos">
          <h2>Fluxo <small>pessoas, canais, regras e agentes · só a intenção</small></h2>
          <div className="mc-fluxo-lista">
            {recentes.map((e) => {
              const d = e.device_ids.find((x) => porDevice.has(x));
              return (
                <button key={e.id} type="button" className="mc-ev" onClick={() => { pulsar([e]); if (d) setSelecao({ tipo: "equipamento", id: d }); }}>
                  <span className="hr">{hora(e.instante)}</span>
                  <span className="tx"><i style={{ background: corDoEvento(e) }} />{e.resumo || e.acao.replace(/_/g, " ")}</span>
                  <span className="rota">{e.ator}{e.canal ? ` · ${e.canal}` : ""}{d ? ` → ${nomeDe(d)}` : ""}</span>
                </button>);
            })}
            {!recentes.length && <p className="mc-vazio">Nenhum evento de pessoa ou agente nas últimas 24 h.</p>}
          </div>
        </section>
      </footer>
    </div>
  );
}

/** Legenda da linha do tempo, nas cores do protótipo (pessoa pelo canal; Sentinela = segurança). */
const LEGENDA_TEMPO: [string, string][] = [["Pessoa (WhatsApp)", COR_EVENTO.humano], ["Pessoa (Telegram)", COR_TELEGRAM], ["Agente de IA", COR_EVENTO.agente],
  ["Regra / aprovação", COR_EVENTO.regra], ["Ronda", COR_EVENTO.ronda], ["Segurança", COR_SEGURANCA], ["Campo", COR_EVENTO.campo], ["Sistema", COR_EVENTO.sistema]];

/** Linha do tempo dos últimos 10 min: as bolinhas andam para a esquerda a cada segundo; marcas a cada 2 min. */
function LinhaDoTempo({ eventos, agora, aoEscolher }: { eventos: EventoLocal[]; agora: number; aoEscolher: (e: EventoLocal) => void }) {
  const marcas = Array.from({ length: JANELA_MIN / 2 + 1 }, (_, i) => i * 2);
  return (
    <section className="mc-tempo" aria-label="Linha do tempo dos últimos 10 minutos">
      <div className="mc-tempo-cab">
        <h2>Últimos {JANELA_MIN} min <small>{eventos.length} evento(s) · clique para rever o pulso</small></h2>
        <div className="mc-tempo-legenda">
          {LEGENDA_TEMPO.map(([nome, cor]) => <span key={nome}><i style={{ background: cor }} />{nome}</span>)}
        </div>
      </div>
      <div className="mc-trilho">
        {marcas.map((m) => (
          <span key={m} className="marca" style={{ left: `${100 - (m / JANELA_MIN) * 100}%` }}>{m === 0 ? "agora" : `−${m} min`}</span>))}
        {eventos.map((e) => {
          const p = 1 - (agora - Date.parse(e.instante ?? "")) / (JANELA_MIN * 60000);
          return (
            <button key={e.id} type="button" className="bola" style={{ left: `${Math.max(0, Math.min(1, p)) * 100}%`, background: corDoEvento(e) }}
              title={`${hora(e.instante)} · ${NOME_TIPO_ATOR[e.tipo_ator]} ${e.ator} · ${e.resumo || e.acao}`}
              aria-label={`${hora(e.instante)}, ${NOME_TIPO_ATOR[e.tipo_ator]} ${e.ator}: ${e.resumo || e.acao}`} onClick={() => aoEscolher(e)} />);
        })}
      </div>
    </section>
  );
}

type ItemEquip = { e: Equipamento; estado: EstadoMapa };
type ItemSetor = { id: string; codigo: string; titulo: string; estado: EstadoMapa; equipamentos: ItemEquip[]; setor: { ind: { pressaoMin: number | null } } };

function Dica({ dica, porDevice, setores }: { dica: { alvo: AlvoCena; x: number; y: number }; porDevice: Map<string, ItemEquip>; setores: ItemSetor[] }) {
  const { alvo } = dica;
  let titulo = alvo.id, sub = "";
  if (alvo.tipo === "equipamento") { const x = porDevice.get(alvo.id); if (x) { titulo = x.e.nome; sub = `${TIPO_NOME[x.e.tipo]} · ${NOME_ESTADO[x.estado]}`; } }
  else if (alvo.tipo === "setor") { const s = setores.find((y) => y.id === alvo.id); if (s) { titulo = s.titulo; sub = `Setor · ${NOME_ESTADO[s.estado]}`; } }
  else sub = alvo.tipo === "agente" ? "Agente de IA" : "Pessoa";
  return <div className="mc-dica" style={{ left: dica.x + 14, top: Math.max(4, dica.y - 48) }}><b>{titulo}</b><span>{sub}</span></div>;
}

function Selecao({ selecao, limpar, porDevice, setores, atores, eventos, valor }: {
  selecao: AlvoCena | null; limpar: () => void; porDevice: Map<string, ItemEquip>; setores: ItemSetor[]; atores: Ator[]; eventos: EventoLocal[];
  valor: (device: string, medida: string) => number | undefined;
}) {
  if (!selecao) return (
    <section className="mc-bloco" aria-live="polite"><h2>Selecionado</h2>
      <p className="mc-vazio">Clique num equipamento, setor, agente, pessoa ou alarme. A câmera vai até ele e aqui aparecem a telemetria, o estado e os últimos eventos.</p>
    </section>);
  let titulo = selecao.id, tipo = "", corpo: ReactNode = null, device: string | null = null, cadastroId: string | null = null;
  if (selecao.tipo === "equipamento") {
    const x = porDevice.get(selecao.id);
    device = selecao.id;
    if (x) {
      if (!x.e.medidas.size) { cadastroId = x.e.ativos[0]?.id ?? null; device = null; }
      titulo = x.e.nome; tipo = TIPO_NOME[x.e.tipo];
      corpo = (<>
        <dl className="mc-kv"><dt>Estado</dt><dd>{NOME_ESTADO[x.estado]}{x.e.nota.motivo ? ` · ${x.e.nota.motivo}` : ""}</dd>
          <dt>Nível de atenção</dt><dd>{NOME_NIVEL[x.e.nota.nivel]}</dd><dt>Setor</dt><dd>{x.e.setor.toUpperCase()}</dd><dt>Dispositivo</dt><dd>{x.e.device}</dd></dl>
        {!x.e.medidas.size && <p className="mc-nota">Sem telemetria: o equipamento está só no cadastro (situação {x.e.ativos[0]?.situacao ?? "—"}). Ele aparece como "sem dados" até algum dispositivo enviar leituras.</p>}
        <div className="mc-lista">
          {[...x.e.medidas].sort().map((m) => { const v = valor(x.e.device, m); return (
            <div key={m} className="mc-ponto"><span>{nomeMedida(m)}</span><span className="v">{v === undefined ? "—" : valorComUnidade(m, v)}</span></div>); })}
        </div></>);
    }
  } else if (selecao.tipo === "setor") {
    const s = setores.find((y) => y.id === selecao.id);
    if (s) {
      titulo = s.titulo; tipo = "Setor de abastecimento";
      corpo = (<>
        <dl className="mc-kv"><dt>Estado</dt><dd>{NOME_ESTADO[s.estado]}</dd><dt>Pressão mínima da rede</dt><dd>{s.setor.ind.pressaoMin === null ? "sem dados" : `${fmt(s.setor.ind.pressaoMin, 1)} mca`}</dd></dl>
        <div className="mc-lista">{s.equipamentos.map(({ e, estado }) => (
          <div key={e.device} className="mc-ponto"><span><span className={`mc-est ${estado}`} /> {e.nome}</span><span className="v">{NOME_ESTADO[estado]}</span></div>))}</div></>);
    }
  } else {
    const a = atores.find((y) => y.ator === selecao.id);
    const m = selecao.tipo === "agente" ? equipeDoMapa(atores).find((y) => y.id === selecao.id) : undefined;
    if (m) titulo = m.nome;
    tipo = selecao.tipo === "agente" ? "Agente de IA · holograma" : "Pessoa";
    corpo = <dl className="mc-kv"><dt>Papel</dt><dd>{m?.papel ?? (a?.papel || "—")}</dd>{m && !m.disponivel && <><dt>Situação</dt><dd>indisponível: {m.motivo}</dd></>}
      <dt>Canais</dt><dd>{a?.canais.join(", ") || "—"}</dd><dt>Eventos em 24 h</dt><dd>{a?.eventos ?? 0}</dd><dt>Conteúdo</dt><dd>só a intenção, nunca o texto</dd></dl>;
  }
  const ultimos = [...eventos].reverse().filter((e) => (device ? e.device_ids.includes(device) : selecao.tipo === "setor"
    ? e.device_ids.some((d) => setores.find((s) => s.id === selecao.id)?.equipamentos.some((x) => x.e.device === d)) : e.ator === selecao.id)).slice(0, 3);
  return (
    <section className="mc-bloco" aria-live="polite">
      <h2>Selecionado</h2>
      <div><div className="mc-det-nome">{titulo}</div><div className="mc-det-tipo">{tipo}</div></div>
      {corpo}
      {ultimos.length > 0 && <p className="mc-nota">Últimos eventos: {ultimos.map((e) => `${hora(e.instante)} ${e.resumo || e.acao}`).join(" · ")}</p>}
      <div className="mc-acoes">
        {device && <Link className="botao-link" to={`/cco/equipamento/${device}`}>Abrir no CCO Clássico</Link>}
        {cadastroId && <Link className="botao-link" to={`/cadastro/${cadastroId}`}>Abrir no cadastro</Link>}
        <button type="button" onClick={limpar}>Limpar seleção</button>
      </div>
      {device && <p className="mc-nota">Manobra só no CCO Clássico, por usuário autorizado.</p>}
    </section>
  );
}
